import React, { useRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowLeftLong,
  faChevronDown,
  faChevronRight,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash-es";
import { Link, NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useHover } from "src/hooks/usehooks-ts";
import { useCurrentPage } from "src/router/useCurrentPage";

type BreadcrumbProps =
  | {
      label: React.ReactNode;
      href: string;
      children?: never;
      dataLoc?: string;
      hideIcon?: boolean;
      active?: boolean;
    }
  | {
      children: React.ReactNode;
      dataLoc?: string;
      hideIcon?: boolean;
      label?: never;
      href?: never;
      active?: never;
    };

export type BreadcrumbType = React.FC<BreadcrumbProps>;

type Props =
  | {
      title: React.ReactNode;
      breadcrumbs?: never;
      navigation?: ArrowNavigationProps;
      sideContent?: React.ReactNode;
      centerSlot?: React.ReactNode;
      backLink?: string;
    }
  | {
      title?: never;
      breadcrumbs: { key: string; breadcrumb: React.ReactNode }[];
      navigation?: ArrowNavigationProps;
      sideContent?: React.ReactNode;
      centerSlot?: React.ReactNode;
      backLink?: string;
    };

type SubComponents = {
  Breadcrumb: BreadcrumbType;
};

type NavigationProps = {
  url: string;
  disabled: boolean;
  tooltip?: string;
};

type ArrowNavigationProps = {
  previous: NavigationProps;
  next: NavigationProps;
};

export const PageHeader: React.FC<Props> & SubComponents = ({
  title,
  breadcrumbs,
  navigation,
  sideContent,
  centerSlot,
  backLink,
}) => {
  const [environment] = useEnvironment();
  const {
    isEntityViewPage,
    isFeaturesPage,
    isEventsPage,
    isEntitiesPage,
    isCasesQueuePage,
    isCaseAnalyticsPage,
    isFlowInsightsPage,
  } = useCurrentPage();
  const showEnvPill =
    isEntityViewPage ||
    isFeaturesPage ||
    isEventsPage ||
    isEntitiesPage ||
    isCasesQueuePage ||
    isCaseAnalyticsPage ||
    isFlowInsightsPage;

  return (
    <div
      className="sticky top-0 z-10 flex h-13 shrink-0 items-center justify-between gap-x-2 border-gray-200 border-b border-l bg-white pl-4"
      data-loc="page-header"
    >
      {backLink && (
        <NavLink data-loc="back-icon" to={backLink}>
          <Icon icon={faArrowLeftLong} size="xs" />
        </NavLink>
      )}
      {title && (
        <h2 className="min-w-0 font-inter-semibold-13px" data-record="true">
          {title}
        </h2>
      )}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <ul
          className="flex min-w-0 max-w-full items-center gap-x-3"
          data-loc="breadcrumbs"
        >
          {breadcrumbs.map(({ key, breadcrumb }) => (
            <React.Fragment key={key}>{breadcrumb}</React.Fragment>
          ))}
        </ul>
      )}
      {navigation && <PageHeaderArrowNavigation {...navigation} />}
      {environment !== "live" && showEnvPill && (
        <Pill dataLoc="environment-pill">
          <Pill.Text>{capitalize(environment)}</Pill.Text>
        </Pill>
      )}
      {centerSlot && (
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          data-loc="page-header-center-slot"
        >
          <div className="pointer-events-auto h-full">{centerSlot}</div>
        </div>
      )}
      <div
        className="ml-auto flex h-full items-center"
        data-loc="page-header-side-content"
      >
        {sideContent}
      </div>
    </div>
  );
};

const PageHeaderBreadcrumb: BreadcrumbType = ({
  label,
  href,
  dataLoc,
  hideIcon,
  active,
  children,
}) => (
  <li className="flex flex-none items-center gap-x-3 truncate last:min-w-0 last:flex-1">
    {!hideIcon && <Icon icon={faChevronRight} size="xs" />}
    {href ? (
      <Link
        className={twMerge(
          "truncate font-inter-semibold-13px text-indigo-600",
          active && "text-gray-800",
        )}
        data-loc={dataLoc}
        to={href}
      >
        {label}
      </Link>
    ) : (
      label
    )}
    {children}
  </li>
);

const PageHeaderArrowNavigation: React.FC<ArrowNavigationProps> = ({
  previous,
  next,
}) => {
  return (
    <div className="flex items-center gap-2">
      <NavigationButton
        icon={faChevronUp}
        disabled={previous.disabled}
        url={previous.url}
        tooltip={previous.tooltip}
      />
      <NavigationButton
        icon={faChevronDown}
        disabled={next.disabled}
        url={next.url}
        tooltip={next.tooltip}
      />
    </div>
  );
};

const NavigationButton: React.FC<{
  icon: IconProp;
  disabled: boolean;
  url: string;
  tooltip: string | undefined;
}> = ({ icon, disabled, url, tooltip }) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const isHovered = useHover(ref);

  return (
    <Tooltip
      title={tooltip}
      asChild
      delay={1000}
      disabled={disabled || !tooltip}
    >
      <Link
        ref={ref}
        className={twMerge(
          "rounded-md border border-gray-200",
          disabled && "pointer-events-none opacity-50",
        )}
        to={url}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : undefined}
      >
        <Icon
          icon={icon}
          size="xs"
          variant={disabled ? "disabled" : isHovered ? "hover" : "default"}
        />
      </Link>
    </Tooltip>
  );
};

PageHeader.Breadcrumb = PageHeaderBreadcrumb;
